export const PREPENDED_LOAD_MAP_NOTE = "__MISSION_START_LOAD_MAP__";
export const PREPENDED_UNDOCK_BEFORE_LOAD_MAP_NOTE = "__PREPEND_UNDOCK_BEFORE_LOAD_MAP__";
export const MISSION_END_DOCK_NOTE = "__MISSION_END_DOCK__";

export const DEFAULT_DOCKING_MISSION_ARGS = {
    standoff_distance: 0.0,
    target_range_m: 0.0,
    show_viewer: false,
    operation: "dock",
} as const;

/** Args for the prepended undock-before-LoadMap bookend. */
export const DEFAULT_UNDOCK_MISSION_ARGS = {
    standoff_distance: 0.0,
    target_range_m: 0.0,
    show_viewer: false,
    operation: "undock",
} as const;

interface MissionActionLike {
    id?: string;
    actionType: string;
    note?: string | null;
    sequenceOrder?: number;
}

const isDockingActionType = (actionType: string): boolean =>
    actionType === "Docking" || actionType === "Dock";

export const isPrependedUndockAction = (action: MissionActionLike): boolean =>
    isDockingActionType(action.actionType) &&
    (action.note ?? "") === PREPENDED_UNDOCK_BEFORE_LOAD_MAP_NOTE;

/** Any Docking that is not the start undock (typically the return dock at mission end). */
export const isMissionEndDockAction = (action: MissionActionLike): boolean =>
    isDockingActionType(action.actionType) && !isPrependedUndockAction(action);

/** True when the mission already has an undock step before the start Load Map. */
export const hasPrependedUndockBeforeLoadMap = (
    actions: MissionActionLike[],
): boolean => actions.some(isPrependedUndockAction);

/** Return-to-dock step pinned at mission end when docking is required. */
export const findMissionEndDockAction = <T extends MissionActionLike>(
    actions: T[],
): T | undefined => {
    const withNote = actions.find(
        (action) =>
            isDockingActionType(action.actionType) &&
            (action.note ?? "") === MISSION_END_DOCK_NOTE,
    );
    if (withNote) return withNote;

    const returnDocks = actions
        .filter(isMissionEndDockAction)
        .slice()
        .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0));
    return returnDocks[returnDocks.length - 1];
};

/** Undock before Load Map + dock at mission end are both present. */
export const missionHasDockingRequired = (actions: MissionActionLike[]): boolean =>
    hasPrependedUndockBeforeLoadMap(actions) && findMissionEndDockAction(actions) != null;

/** Explicit Undock step, or the prepended Docking undock-before-LoadMap bookend. */
export const isUndockMissionAction = (action: MissionActionLike): boolean =>
    action.actionType === "Undock" || isPrependedUndockAction(action);

/**
 * True when a remaining mission step will undock (from startStepIndex onward).
 * Used to skip the docked charging safety gate — the mission handles undock itself.
 */
export const missionHasUpcomingUndock = (
    actions: MissionActionLike[],
    startStepIndex = 0,
): boolean => {
    const start = Math.max(0, Math.floor(startStepIndex));
    return actions.some(
        (action) => (action.sequenceOrder ?? 0) >= start && isUndockMissionAction(action),
    );
};
